import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { insertAuditLog, ACTION_TYPES } from '../../../services/auditLogService';

const PendingInvitationsTab = ({ invitations, onInvitationUpdated }) => {
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(null);

  const handleResendInvitation = async () => {
    const invitation = resetConfirm;
    if (!invitation) return;
    setActionLoading(invitation?.id);
    try {
      const newExpiresAt = new Date();
      newExpiresAt?.setDate(newExpiresAt?.getDate() + 7);

      const { error } = await supabase?.from('invitations')?.update({
        expires_at: newExpiresAt?.toISOString(),
        status: 'pending'
      })?.eq('id', invitation?.id);

      if (error) throw error;

      await insertAuditLog({
        actionType: ACTION_TYPES?.INVITATION_SENT,
        affectedUserEmail: invitation?.email,
        description: `Invitation reset and resent to ${invitation?.email}`,
        metadata: { invitation_id: invitation?.id, role: invitation?.role, action: 'resend' },
      });

      setResetConfirm(null);
      onInvitationUpdated();
    } catch (error) {
      console.error('Error resending invitation:', error?.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInvitation = async () => {
    const invitation = cancelConfirm;
    if (!invitation) return;
    setActionLoading(invitation?.id);
    try {
      const { error } = await supabase?.from('invitations')?.update({ status: 'revoked' })?.eq('id', invitation?.id);

      if (error) throw error;

      await insertAuditLog({
        actionType: 'Invitation Cancelled',
        affectedUserEmail: invitation?.email,
        description: `Pending invitation cancelled for ${invitation?.email}`,
        metadata: { invitation_id: invitation?.id, role: invitation?.role, action: 'cancel' },
      });

      setCancelConfirm(null);
      onInvitationUpdated();
    } catch (error) {
      console.error('Error canceling invitation:', error?.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteInvitation = async () => {
    if (!deleteConfirm) return;
    setActionLoading(deleteConfirm?.id);
    try {
      await insertAuditLog({
        actionType: 'Invitation Deleted',
        affectedUserEmail: deleteConfirm?.email,
        description: `Invitation permanently deleted for ${deleteConfirm?.email}`,
        metadata: { invitation_id: deleteConfirm?.id, role: deleteConfirm?.role, action: 'delete' },
      });

      const { error } = await supabase?.from('invitations')?.delete()?.eq('id', deleteConfirm?.id);
      if (error) throw error;
      setDeleteConfirm(null);
      onInvitationUpdated();
    } catch (error) {
      console.error('Error deleting invitation:', error?.message);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (invitation) => {
    const now = new Date();
    const expiresAt = new Date(invitation?.expires_at);
    const isExpired = expiresAt < now;

    if (invitation?.status === 'accepted') {
      return { label: 'Accepted', color: 'bg-green-100 text-green-700' };
    }
    if (invitation?.status === 'revoked') {
      return { label: 'Cancelled', color: 'bg-red-100 text-red-700' };
    }
    if (isExpired) {
      return { label: 'Expired', color: 'bg-orange-100 text-orange-700' };
    }
    return { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' };
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'administrator': return 'bg-purple-100 text-purple-700';
      case 'user': return 'bg-blue-100 text-blue-700';
      case 'view_only': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isExpired = (invitation) => new Date(invitation?.expires_at) < new Date();

  const canReset = (invitation) => {
    return invitation?.status === 'pending' || isExpired(invitation);
  };

  const canCancel = (invitation) => {
    return invitation?.status === 'pending' && !isExpired(invitation);
  };

  const canDelete = (invitation) => {
    // Admins can delete any invitation regardless of status
    return true;
  };

  return (
    <div className="space-y-4">
      {invitations?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Mail" size={48} className="text-muted-foreground mb-4" />
          <p className="text-lg font-medium text-foreground">No invitations sent yet</p>
          <p className="text-sm text-muted-foreground">Click "Invite User" to send your first invitation</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3">
                  <span className="text-xs font-semibold text-foreground">Email</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className="text-xs font-semibold text-foreground">Role</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className="text-xs font-semibold text-foreground">Invited By</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className="text-xs font-semibold text-foreground">Sent Date</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className="text-xs font-semibold text-foreground">Expires</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className="text-xs font-semibold text-foreground">Status</span>
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-foreground">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invitations?.map((invitation) => {
                const statusBadge = getStatusBadge(invitation);
                return (
                  <tr key={invitation?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="Mail" size={16} className="text-accent" />
                        </div>
                        <p className="text-sm font-medium text-foreground">{invitation?.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(invitation?.role)}`}>
                        {invitation?.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">
                        {invitation?.inviter?.full_name || invitation?.inviter?.email || 'System'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{formatDate(invitation?.created_at)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{formatDate(invitation?.expires_at)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge?.color}`}>
                        {statusBadge?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {canReset(invitation) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setResetConfirm(invitation)}
                            disabled={actionLoading === invitation?.id}
                          >
                            <Icon name="RefreshCw" size={14} className="mr-1" />
                            Reset
                          </Button>
                        )}
                        {canCancel(invitation) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCancelConfirm(invitation)}
                            disabled={actionLoading === invitation?.id}
                          >
                            <Icon name="X" size={14} className="mr-1" />
                            Cancel
                          </Button>
                        )}
                        {canDelete(invitation) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(invitation)}
                            disabled={actionLoading === invitation?.id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Icon name="Trash2" size={14} className="mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      {resetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setResetConfirm(null)} />
          <div className="relative bg-background rounded-lg shadow-xl border border-border p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Icon name="RefreshCw" size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Reset Invitation</h3>
                <p className="text-sm text-muted-foreground">Resend with a new 7-day expiry</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-6">
              Reset and resend the invitation to{' '}
              <span className="font-medium">{resetConfirm?.email}</span>? The expiry will be extended by 7 days from today.
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setResetConfirm(null)}
                disabled={actionLoading === resetConfirm?.id}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleResendInvitation}
                disabled={actionLoading === resetConfirm?.id}
                loading={actionLoading === resetConfirm?.id}
              >
                <Icon name="RefreshCw" size={14} className="mr-1" />
                Reset & Resend
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {cancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCancelConfirm(null)} />
          <div className="relative bg-background rounded-lg shadow-xl border border-border p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Icon name="XCircle" size={20} className="text-orange-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Cancel Invitation</h3>
                <p className="text-sm text-muted-foreground">The invite link will no longer work</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-6">
              Cancel the pending invitation for{' '}
              <span className="font-medium">{cancelConfirm?.email}</span>? They will not be able to use the existing invite link.
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCancelConfirm(null)}
                disabled={actionLoading === cancelConfirm?.id}
              >
                Keep Invitation
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCancelInvitation}
                disabled={actionLoading === cancelConfirm?.id}
                loading={actionLoading === cancelConfirm?.id}
                className="bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
              >
                <Icon name="XCircle" size={14} className="mr-1" />
                Cancel Invitation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-background rounded-lg shadow-xl border border-border p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Delete Invitation</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-3">
              Permanently delete the invitation for{' '}
              <span className="font-medium">{deleteConfirm?.email}</span>? This will remove all records of this invitation.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex items-start gap-2">
              <Icon name="Info" size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                After deletion, <strong>{deleteConfirm?.email}</strong> can be invited again as a new user.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading === deleteConfirm?.id}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleDeleteInvitation}
                disabled={actionLoading === deleteConfirm?.id}
                loading={actionLoading === deleteConfirm?.id}
                className="bg-red-600 hover:bg-red-700 text-white border-red-600"
              >
                <Icon name="Trash2" size={14} className="mr-1" />
                Delete Permanently
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingInvitationsTab;